import { Autocomplete, Box, TextField } from '@mui/material'
import debounce from 'lodash/debounce'
import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'

import { useAppDispatch, useAppSelector } from 'redux/Hook'
import { setToolBuilder } from 'redux/slices/ToolBuilder'
import { generatePermaLink } from 'redux/thunks/ToolBuilder'

interface PermaLinkProps {
  id: string
  label: string
  error?: boolean
  helperText?: string
  required?: boolean
  parentValue?: string
  inputProps?: React.InputHTMLAttributes<HTMLInputElement>
}

const PermaLink = (props: PermaLinkProps) => {
  const dispatch = useAppDispatch()
  const { id, label, required, parentValue, inputProps } = props
  const { t } = useTranslation()

  const options = useAppSelector(
    (state) => state.toolBuilder.lookups?.permaLink,
  )
  const selectedValue =
    useAppSelector((state) => state.toolBuilder?.toolBuilder?.[id]) || ''
  const errorMessage = useAppSelector(
    (state) => state.toolBuilder.validation?.[id],
  )

  const debouncedSearch = debounce((value) => {
    dispatch(generatePermaLink({ value }))
  }, 500)

  const handleInputChange = (
    event: React.SyntheticEvent,
    value: any,
    reason: string,
  ) => {
    if (reason === 'input' && value && value.trim().length > 1) {
      debouncedSearch(value)
    }
  }

  useEffect(() => {
    if (parentValue && parentValue.trim().length > 1 && !selectedValue) {
      debouncedSearch(parentValue)
    }
  }, [parentValue])

  return (
    <Box>
      <Autocomplete
        id={id}
        options={options ?? []}
        filterOptions={(options) => options}
        value={selectedValue}
        onChange={(event, newValue) => {
          dispatch(setToolBuilder({ name: id, value: newValue }))
        }}
        getOptionLabel={(option: any) => option || ''}
        onInputChange={handleInputChange}
        renderOption={(props, option) => (
          <li {...props} key={option}>
            {option}
          </li>
        )}
        renderInput={(params) => (
          <TextField
            {...params}
            variant="outlined"
            label={label}
            required={required}
            error={!!errorMessage}
            helperText={errorMessage}
            inputProps={{
              ...params.inputProps,
              ...inputProps,
            }}
          />
        )}
        noOptionsText={t('toolBuilder.noOptionsText')}
      />
    </Box>
  )
}

export default PermaLink
export type { PermaLinkProps }
